import React from 'react';
import { X, Heart, Copy, Check, ExternalLink } from 'lucide-react';

export default function SavedCodesSidebar({ 
  isOpen, 
  onClose, 
  savedCodes, 
  allCodes, 
  onUnsave,
  onCopy,
  copiedId 
}) {
  // Filter to get only saved codes with full details
  const savedCodeDetails = allCodes.filter(code => savedCodes.has(code.id));

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-6 h-6 fill-current" />
              <h2 className="text-2xl font-bold">Saved Codes</h2>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-sm mt-2 opacity-90">{savedCodeDetails.length} codes saved</p>
        </div>

        {/* Codes List */}
        <div className="overflow-y-auto h-[calc(100%-120px)] p-4">
          {savedCodeDetails.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-semibold">No saved codes yet</p>
              <p className="text-gray-400 text-sm mt-2">Click the heart on any code to save it!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {savedCodeDetails.map(code => (
                <div 
                  key={code.id}
                  className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-500"
                >
                  {/* Website */}
                  <h3 className="font-bold text-lg text-gray-900">{code.website}</h3>
                  <p className="text-sm text-gray-500 mb-2">{code.category}</p>

                  {/* Discount */}
                  <p className="text-purple-600 font-semibold mb-3">{code.discount}</p>

                  {/* Code */}
                  <div className="flex items-center gap-2 bg-white rounded-lg p-3 border border-gray-200 mb-3">
                    <code className="flex-1 font-mono font-bold text-gray-900">{code.code}</code>
                    <button
                      onClick={() => onCopy(code.code, code.id)}
                      className="p-2 hover:bg-gray-100 rounded"
                    >
                      {copiedId === code.id ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <Copy className="w-5 h-5 text-purple-600" />
                      )}
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => onUnsave(code.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition text-sm font-medium"
                    >
                      <Heart className="w-4 h-4 fill-current" />
                      Remove
                    </button>
                    <a
                      href={code.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition text-sm font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Video
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}